import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { rsvpApi } from "../../api/rsvp";
import { walkInApi } from "../../api/walkIn";
import { PublicTierSignInForm } from "../../components/events/PublicTierSignInForm";
import { PublicTicketTemplateShell } from "../../components/ticketTemplate/PublicTicketTemplateShell";
import "../../components/ticketTemplate/ticketTemplate.css";
import "../../components/ticketTemplate/zoniqTicketTemplate.css";
import { queryKeys } from "../../queryKeys";
import { DEFAULT_TICKET_TEMPLATE_CONFIG } from "../../types/rsvp";
import type { PayPreference, RsvpGuest } from "../../types/rsvp";
import { mergeTemplateConfig } from "../../utils/ticketTemplateStore";

/** Public Walk-in sign-in form (Sprint 5) — same ZONiQ ticket template shell as RSVP/Guest List. */
const PublicWalkInPage: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const [done, setDone] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [result, setResult] = useState<RsvpGuest | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const metaQuery = useQuery({
    queryKey: inviteToken ? queryKeys.publicWalkIn(inviteToken) : ["walk-in", "idle"],
    queryFn: () => walkInApi.publicMeta(inviteToken!),
    enabled: Boolean(inviteToken),
    retry: 1,
  });

  const submitMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      phone: string;
      email?: string;
      tier_counts: Record<string, number>;
      party_size: number;
      whatsapp_opt_in: boolean;
      pay_preference?: PayPreference;
    }) => {
      if (!inviteToken) throw new Error("Missing invite");
      // Straight to the RSVP API (the walk-in adapter drops pay_url / pass_url).
      return rsvpApi.publicSubmit(inviteToken, {
        name: values.name,
        phone: values.phone,
        email: values.email ?? null,
        party_size: values.party_size,
        category: "stag",
        tier_counts: values.tier_counts,
        whatsapp_opt_in: values.whatsapp_opt_in,
        pay_preference: values.pay_preference,
      });
    },
    onSuccess: (data, values) => {
      setGuestName(values.name);
      setResult(data);
      if (data.pay_url) {
        setRedirecting(true);
        window.location.assign(data.pay_url);
        return;
      }
      setDone(true);
      toast.success("Walk-in registered");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not submit"),
  });

  if (!inviteToken) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Invalid Walk-in link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isPending) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Loading event…</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isError || !metaQuery.data) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body zticket-status">
            <h2 className="zticket-status__title">Link unavailable</h2>
            <p className="zticket-status__text">
              {metaQuery.error instanceof Error
                ? metaQuery.error.message
                : "This Walk-in link is invalid or has expired."}
            </p>
            <Link to="/" className="eventos-nav-btn">
              Go to Zoniq
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const meta = metaQuery.data;
  const templateId = meta.ticket_template_id ?? "black";
  const templateConfig = mergeTemplateConfig(meta.template_config ?? DEFAULT_TICKET_TEMPLATE_CONFIG);

  if (redirecting) {
    return (
      <PublicTicketTemplateShell
        templateId={templateId}
        config={templateConfig}
        meta={{ ...meta, link_kind: "walk_in" }}
        eyebrow="Walk-in"
      >
        <div className="zticket-status">
          <h2 className="zticket-status__title">Taking you to payment…</h2>
          <p className="zticket-status__text">Please don&apos;t close this page.</p>
        </div>
      </PublicTicketTemplateShell>
    );
  }

  if (done) {
    return (
      <PublicTicketTemplateShell
        templateId={templateId}
        config={templateConfig}
        meta={{ ...meta, link_kind: "walk_in" }}
        eyebrow="Walk-in"
      >
        <div className="zticket-status">
          <h2 className="zticket-status__title">You&apos;re signed in</h2>
          <p className="zticket-status__text">
            Thanks, {guestName || "guest"}. See you at {meta.event_name}.
          </p>
          {result?.pass_url ? (
            <a className="eventos-primary-btn" href={result.pass_url}>
              Open my entry pass
            </a>
          ) : null}
        </div>
      </PublicTicketTemplateShell>
    );
  }

  return (
    <PublicTicketTemplateShell
      templateId={templateId}
      config={templateConfig}
      meta={{ ...meta, link_kind: "walk_in" }}
      eyebrow="Walk-in"
    >
      <PublicTierSignInForm
        tiers={meta.ticket_tiers ?? []}
        showPricing={(meta.payment_mode ?? "free") !== "free"}
        paymentMode={meta.payment_mode ?? "free"}
        submitLabel="Register walk-in"
        pending={submitMutation.isPending}
        onSubmit={(values) => submitMutation.mutate(values)}
        submitError={submitMutation.error}
      />
    </PublicTicketTemplateShell>
  );
};

export default PublicWalkInPage;
